document.documentElement.classList.remove('no-js');

const qs = (selector, scope = document) => scope.querySelector(selector);
const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];

// A closed drawer is off-canvas but still in the document, so mark it inert: that removes its
// controls from the tab order synchronously, without depending on a CSS transition finishing.
qsa('[data-drawer]').forEach((drawer) => { drawer.inert = !drawer.hasAttribute('open'); });

let activeToggle = null;

function closeDrawers() {
  const returning = activeToggle;
  activeToggle = null;
  qsa('[data-drawer]').forEach((drawer) => {
    drawer.removeAttribute('open');
    drawer.inert = true;
  });
  qsa('[data-drawer-toggle]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
  const scrim = qs('[data-drawer-scrim]');
  if (scrim) scrim.hidden = true;
  document.body.classList.remove('drawer-open');
  // Give focus back to the control that opened the drawer, but only when it is not already on
  // something the visitor chose deliberately.
  const stranded = document.activeElement === document.body
    || qsa('[data-drawer]').some((drawer) => drawer.contains(document.activeElement));
  if (returning && stranded) returning.focus();
}

qsa('[data-drawer-toggle]').forEach((button) => {
  button.addEventListener('click', () => {
    const drawer = qs(button.dataset.drawerToggle);
    if (!drawer) return;
    const opening = !drawer.hasAttribute('open');
    closeDrawers();
    if (opening) {
      drawer.setAttribute('open', '');
      drawer.inert = false;
      button.setAttribute('aria-expanded', 'true');
      const scrim = qs('[data-drawer-scrim]');
      if (scrim) scrim.hidden = false;
      document.body.classList.add('drawer-open');
      activeToggle = button;
      qs('a,button', drawer)?.focus();
    }
  });
});

qs('[data-drawer-scrim]')?.addEventListener('click', closeDrawers);
document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeDrawers(); });

// Desktop navigation opens on hover while touch devices keep the native
// <details>/<summary> click behavior. The short close delay lets the pointer
// cross the small visual gap between a menu label and its dropdown.
const desktopHover = window.matchMedia('(hover: hover) and (pointer: fine)');
const desktopMenus = qsa('.desktop-nav details.nav-item');

function closeDesktopMenus(except = null) {
  desktopMenus.forEach((menu) => {
    if (menu !== except) menu.open = false;
  });
}

desktopMenus.forEach((menu) => {
  const summary = qs('summary', menu);
  let pointerInside = false;
  let closeTimer = null;

  const cancelClose = () => {
    if (closeTimer) clearTimeout(closeTimer);
    closeTimer = null;
  };
  const openMenu = () => {
    cancelClose();
    closeDesktopMenus(menu);
    menu.open = true;
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer = setTimeout(() => {
      if (!pointerInside) menu.open = false;
    }, 140);
  };

  menu.addEventListener('mouseenter', () => {
    if (!desktopHover.matches) return;
    pointerInside = true;
    openMenu();
  });
  menu.addEventListener('mouseleave', () => {
    if (!desktopHover.matches) return;
    pointerInside = false;
    scheduleClose();
  });
  menu.addEventListener('focusin', openMenu);
  menu.addEventListener('focusout', () => {
    requestAnimationFrame(() => {
      if (!pointerInside && !menu.contains(document.activeElement)) menu.open = false;
    });
  });
  summary?.addEventListener('click', (event) => {
    if (!desktopHover.matches) return;
    event.preventDefault();
    openMenu();
  });
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closeDesktopMenus();
});

qsa('[data-quantity]').forEach((control) => {
  const input = qs('input', control);
  qsa('button', control).forEach((button) => button.addEventListener('click', () => {
    const step = button.dataset.action === 'increase' ? 1 : -1;
    input.value = Math.max(Number(input.min || 1), Number(input.value || 1) + step);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }));
});

qsa('.product-form').forEach((form) => {
  const select = qs('[name="id"]', form);
  const button = qs('[type="submit"]', form);
  select?.addEventListener('change', () => {
    const option = select.options[select.selectedIndex];
    if (!button) return;
    button.disabled = option?.disabled;
    button.textContent = option?.disabled ? 'Sold out' : 'Add to bag';
  });
});

qsa('[data-filter-form] input, [data-filter-form] select').forEach((input) => {
  input.addEventListener('change', () => input.form?.submit());
});

// ---- Product page v4: option selectors resolve to the exact Shopify variant ----
// Variant data comes from the section's own JSON of product.variants, so nothing is
// hardcoded per product, color, SKU, or position. Unpublished-for-channel variants are
// simply absent from that storefront list; sold-out variants stay selectable-visible
// but disabled and labelled, never silently purchasable.
function formatMoney(cents) {
  if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
    try { return window.Shopify.formatMoney(cents, '{{ amount }}'); } catch (e) { /* fall through */ }
  }
  return '$' + (cents / 100).toFixed(2);
}

qsa('.product-form').forEach((form) => {
  const jsonEl = qs('[data-variant-json]', form);
  const idInput = qs('[data-variant-id]', form);
  const optionSelects = qsa('[data-option-index]', form);
  if (!jsonEl || !idInput || !optionSelects.length) return;
  let variants = [];
  try { variants = JSON.parse(jsonEl.textContent); } catch (e) { return; }
  if (!Array.isArray(variants) || !variants.length) return;

  const jsOnly = qs('.js-only', form);
  if (jsOnly) jsOnly.hidden = false;

  const scope = form.closest('.product-details') || form;
  const button = qs('[data-add-button]', form);
  const priceValue = qs('[data-price-value]', scope);
  const priceWrap = qs('[data-price]', scope);

  const valueFor = (select) => select.value;
  const matches = (variant, overrides) => optionSelects.every((select) => {
    const idx = Number(select.dataset.optionIndex);
    const want = overrides && overrides.select === select ? overrides.value : valueFor(select);
    return variant.options[idx] === want;
  });
  const findVariant = (overrides) => variants.find((v) => matches(v, overrides)) || null;

  function refreshOptionStates() {
    optionSelects.forEach((select) => {
      Array.from(select.options).forEach((opt) => {
        if (!opt.dataset.baseText) opt.dataset.baseText = opt.textContent;
        const candidate = findVariant({ select, value: opt.value });
        const soldOut = !!candidate && !candidate.available;
        opt.disabled = !candidate || soldOut;
        opt.textContent = opt.dataset.baseText + (soldOut ? ' — Sold out' : '');
      });
      // Keep the current selection legal even if the matrix changed underneath it.
      if (select.selectedIndex === -1 && select.options.length) select.selectedIndex = 0;
    });
  }

  function apply(updateUrl) {
    const variant = findVariant();
    if (!variant) return;
    idInput.value = String(variant.id);
    if (priceValue) priceValue.textContent = formatMoney(variant.price);
    if (priceWrap) {
      let compare = qs('[data-compare-value]', priceWrap);
      if (variant.compare_at_price > variant.price) {
        if (!compare) {
          compare = document.createElement('s');
          compare.setAttribute('data-compare-value', '');
          priceWrap.appendChild(compare);
        }
        compare.textContent = formatMoney(variant.compare_at_price);
      } else if (compare) {
        compare.remove();
      }
    }
    if (button) {
      button.disabled = !variant.available;
      button.textContent = variant.available ? 'Add to bag' : 'Sold out';
    }
    if (updateUrl) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState(null, '', url.toString());
      } catch (e) { /* URL update is progressive enhancement only */ }
    }
  }

  optionSelects.forEach((select) => select.addEventListener('change', () => {
    refreshOptionStates();
    apply(true);
  }));
  refreshOptionStates();
  apply(false);
});

// ---- Cart v5: required final-sale agreement gate (client-side only) ----
// The checkbox is never prechecked, and this cart form does a full page reload on a
// quantity update, so an update can never leave the gate falsely agreed. This is a
// storefront convenience gate, not a server-side checkout block.
qsa('[data-cart-form]').forEach((form) => {
  const box = qs('[data-final-sale-checkbox]', form);
  const checkout = qs('[data-checkout-button]', form);
  const error = qs('[data-final-sale-error]', form);
  if (!box || !checkout) return;
  const sync = () => {
    checkout.disabled = !box.checked;
    checkout.setAttribute('aria-disabled', String(!box.checked));
    if (box.checked && error) error.hidden = true;
  };
  box.addEventListener('change', sync);
  form.addEventListener('submit', (event) => {
    const submitter = event.submitter;
    const isUpdate = !!submitter && submitter.name === 'update';
    if (!isUpdate && !box.checked) {
      event.preventDefault();
      if (error) error.hidden = false;
      box.focus();
    }
  });
  sync();
});

// ---- v5: Bougie promotional popup (default off; opt-in via section settings) ----
(function () {
  const popup = qs('[data-promo-popup]');
  if (!popup) return;
  const LS_PREFIX = 'bougie_popup_dismissed_';
  const key = popup.dataset.campaignKey || 'default';
  const scrim = qs('[data-popup-scrim]', popup);
  const closeBtn = qs('[data-popup-close]', popup);
  const panel = qs('.promo-popup__panel', popup);
  const persistBox = qs('[data-popup-dismiss-persist]', popup);
  const cta = qs('[data-popup-cta]', popup);
  const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
  let lastFocus = null;
  let showTimer = null;
  let cdTimer = null;

  const storeDismiss = () => { try { localStorage.setItem(LS_PREFIX + key, String(Date.now())); } catch (e) { /* storage unavailable */ } };
  const isDismissed = () => { try { return !!localStorage.getItem(LS_PREFIX + key); } catch (e) { return false; } };
  const parseISO = (s) => { if (!s) return null; const t = Date.parse(s); return Number.isNaN(t) ? null : t; };

  function deviceAllowed() {
    const coarse = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    const mobile = coarse || window.innerWidth < 750;
    return mobile ? popup.dataset.showMobile === 'true' : popup.dataset.showDesktop === 'true';
  }
  function withinWindow() {
    const start = parseISO(popup.dataset.start);
    const end = parseISO(popup.dataset.end);
    const now = Date.now();
    if (start && now < start) return false;
    if (end && now > end) return false;
    return true;
  }
  // Avoid stacking on top of another modal (e.g. a Shopify Forms popup): if one is
  // already visible, do not open ours.
  function otherModalOpen() {
    return qsa('[role="dialog"],[aria-modal="true"]')
      .some((d) => d !== popup && !d.hidden && d.offsetParent !== null);
  }
  function tickCountdown() {
    const cd = qs('[data-popup-countdown]', popup);
    if (!cd) return;
    const end = parseISO(popup.dataset.end);
    if (!end) return;
    let diff = Math.max(0, end - Date.now());
    const d = Math.floor(diff / 86400000); diff -= d * 86400000;
    const h = Math.floor(diff / 3600000); diff -= h * 3600000;
    const m = Math.floor(diff / 60000); diff -= m * 60000;
    const s = Math.floor(diff / 1000);
    const set = (sel, v) => { const el = qs(sel, cd); if (el) el.textContent = String(v).padStart(2, '0'); };
    set('[data-cd-days]', d); set('[data-cd-hours]', h); set('[data-cd-min]', m); set('[data-cd-sec]', s);
    if (end - Date.now() <= 0) {
      if (cdTimer) { clearInterval(cdTimer); cdTimer = null; }
      cd.textContent = popup.dataset.expiredText || 'This promotion has ended.';
    }
  }
  function onKeydown(event) {
    if (event.key === 'Escape') { event.preventDefault(); closePopup(true); return; }
    if (event.key !== 'Tab') return;
    const items = qsa(FOCUSABLE, panel).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }
  function openPopup() {
    if (!popup.hidden) return;
    if (otherModalOpen()) return;
    lastFocus = document.activeElement;
    popup.hidden = false;
    document.body.classList.add('popup-open');
    if (popup.dataset.countdown === 'true') { tickCountdown(); cdTimer = setInterval(tickCountdown, 1000); }
    document.addEventListener('keydown', onKeydown);
    if (closeBtn) closeBtn.focus();
  }
  function closePopup(persist) {
    if (popup.hidden) return;
    popup.hidden = true;
    document.body.classList.remove('popup-open');
    document.removeEventListener('keydown', onKeydown);
    if (cdTimer) { clearInterval(cdTimer); cdTimer = null; }
    if (persist || (persistBox && persistBox.checked)) storeDismiss();
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  }

  if (closeBtn) closeBtn.addEventListener('click', () => closePopup(true));
  if (scrim) scrim.addEventListener('click', () => closePopup(false));
  if (cta) cta.addEventListener('click', () => closePopup(true));

  if (isDismissed() || !deviceAllowed() || !withinWindow()) return;
  const delay = Math.max(0, parseInt(popup.dataset.delay || '0', 10)) * 1000;
  showTimer = setTimeout(openPopup, delay);
})();
